<template>
    <v-container fluid>
        <v-container grid-list-md class="no-padding-mobile no-padding-bottom">
            <v-layout row wrap align-center>
                <v-flex xs12 lg12>
                    <h1 class="display-1">{{ $t("title.stats") }}</h1>
                    <TrapFilter
                        :categories.sync="categoryFilter"
                        :plan.sync="planFilter"
                        hide-status
                    />
                </v-flex>
            </v-layout>

            <BaseDatePicker
                :start.sync="dateRange.start"
                :end.sync="dateRange.end"
                :range.sync="range"
            >
                <v-progress-circular
                    slot="loader"
                    v-if="$apollo.queries.trapsStats.loading"
                    :size="30"
                    color="primary"
                    indeterminate
                ></v-progress-circular>
            </BaseDatePicker>
            <v-btn @click="exportStats" v-t="'button.csv_export'" :loading="exportLoading"></v-btn>
        </v-container>
        <v-tabs
            v-model="tabActive"
            dark
            slider-color="white"
            v-if="trapsStats"
            class="no-margin-mobile"
        >
            <v-tab href="#catches" ripple>{{ $t("tab.chart_catches") }}</v-tab>
            <v-tab href="#battery" ripple>{{ $t("tab.chart_battery") }}</v-tab>
            <v-tab href="#signal" ripple>{{ $t("tab.chart_signal") }}</v-tab>
            <v-tab href="#temp" ripple>{{ $t("tab.chart_temperatures") }}</v-tab>
            <v-tab href="#totals" ripple>{{ $t("tab.chart_totals") }}</v-tab>
            <v-tab-item value="catches">
                <v-card text>
                    <v-card-text class="text--primary">
                        <div class="Chart">
                            <ChartBar
                                v-if="tabActive == 'catches'"
                                :chart-data="stats"
                                :options="optionsCatch"
                                :style="{ height: '50vh', minHeight: chartHeight + 'px' }"
                            />
                        </div>
                    </v-card-text>
                </v-card>
            </v-tab-item>
            <v-tab-item value="battery">
                <v-card text>
                    <v-card-text class="text--primary">
                        <div class="Chart">
                            <ChartLine
                                v-if="tabActive == 'battery'"
                                :chart-data="statsBattery"
                                :options="optionsAvg"
                                :style="{ height: '50vh', minHeight: chartHeight + 'px' }"
                            />
                        </div>
                        <div class="Chart">
                            <h2>{{ $t("label.average_all_traps") }}</h2>
                            <ChartLineBattery
                                v-if="tabActive == 'battery'"
                                :chart-data="globalBatteryStats"
                                :options="
                                    Object.assign({}, optionsAvg, { legend: { display: false } })
                                "
                                :style="{ height: '350px' }"
                            />
                        </div>
                    </v-card-text>
                </v-card>
            </v-tab-item>
            <v-tab-item value="signal">
                <v-card flat>
                    <v-card-text class="text--primary">
                        <div class="Chart">
                            <ChartLine
                                v-if="tabActive == 'signal'"
                                :chart-data="statsSignal"
                                :options="optionsAvg"
                                :style="{ height: '50vh', minHeight: chartHeight + 'px' }"
                            />
                        </div>
                        <div class="Chart">
                            <h2>{{ $t("label.average_all_traps") }}</h2>
                            <ChartLineBattery
                                v-if="tabActive == 'signal'"
                                :chart-data="globalSignalStats"
                                :options="
                                    Object.assign({}, optionsAvg, { legend: { display: false } })
                                "
                                :style="{ height: '350px' }"
                            />
                        </div>
                    </v-card-text>
                </v-card>
            </v-tab-item>
            <v-tab-item value="temp">
                <v-card flat>
                    <v-card-text class="text--primary">
                        <div class="Chart">
                            <ChartLine
                                v-if="tabActive == 'temp'"
                                :chart-data="statsTemperatures"
                                :options="optionsAvgCelcius"
                                :style="{ height: '50vh', minHeight: chartHeight + 'px' }"
                            />
                        </div>
                        <div class="Chart">
                            <h2>{{ $t("label.average_all_traps") }}</h2>
                            <ChartLineTemperature
                                v-if="tabActive == 'temp'"
                                :chart-data="globalTemperatureStats"
                                :options="
                                    Object.assign({}, optionsAvgCelcius, {
                                        legend: { display: false },
                                    })
                                "
                                :style="{ height: '350px' }"
                            />
                        </div>
                    </v-card-text>
                </v-card>
            </v-tab-item>
            <v-tab-item value="totals">
                <v-card flat>
                    <v-card-text class="text--primary">
                        <i18n path="label.has_been_catched" tag="div" class="headline">
                            <v-chip place="n" class="title" dark color="primary">{{
                                totalCatched
                            }}</v-chip>
                            <strong place="start">{{
                                dateRange.start | dateFormat({ month: "short" }, fullLocale)
                            }}</strong>
                            <strong place="end">{{
                                dateRange.end | dateFormat({ month: "short" }, fullLocale)
                            }}</strong>
                        </i18n>

                        <v-layout wrap>
                            <v-flex xs12 lg7 xl8>
                                <div class="Chart">
                                    <h2>{{ $t("title.chart_total_catches") }}</h2>
                                    <ChartDoughnut
                                        v-if="tabActive == 'totals'"
                                        :chart-data="statsTotalCatches"
                                        :style="{
                                            height: '40vh',
                                            minHeight: chartHeight - 100 + 'px',
                                        }"
                                    />
                                </div>
                            </v-flex>
                            <v-flex xs12 lg5 xl4>
                                <v-data-table
                                    :headers="dataTableHeaders"
                                    :items="trapsStats"
                                    class="elevation-1"
                                    item-key="trap._id"
                                    hide-default-footer
                                >
                                    <template v-slot:items="props">
                                        <td>{{ props.item.trap.name }}</td>
                                        <td>
                                            <v-chip color="primary" dark>{{
                                                props.item.totalCatches
                                            }}</v-chip>
                                        </td>
                                    </template>
                                </v-data-table>
                            </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-card>
            </v-tab-item>
        </v-tabs>

        <v-layout v-else justify-center align-center class="headline">{{
            $t("message.no_results")
        }}</v-layout>
        <br />
    </v-container>
</template>

<script>
import ChartBar from "@/components/charts/Bar"
import ChartLine from "@/components/charts/Line"
import ChartLineBattery from "@/components/charts/LineBattery"
import ChartLineTemperature from "@/components/charts/LineTemperature"

import ChartDoughnut from "@/components/charts/Doughnut"
import TrapFilter from "@/components/TrapFilter"
import randomColor from "randomcolor"
import stats from "@/mixins/stats.js"
import throttle from "lodash.throttle"
import download from "@/utils/download.js"
import { mapGetters } from "vuex"
export default {
    mixins: [stats],
    components: {
        ChartBar,
        ChartLine,
        ChartDoughnut,
        TrapFilter,
        ChartLineTemperature,
        ChartLineBattery,
    },
    data() {
        return {
            categoryFilter: [],
            pagination: { sortBy: "totalCatches" },
            planFilter: null,
            dateRange: { start: null, end: null },
            range: "week",
            tabActive: "catches",
            chartHeight: "300px",
            colors: {},
            exportLoading: false,
            dataTableHeaders: [
                {
                    text: this.$t("label.name"),
                    sortable: true,
                    value: "trap.name",
                },
                { text: this.$t("label.total_catches"), value: "totalCatches", sortable: true },
            ],
        }
    },
    mounted() {
        this.big = true
        this.$moment.locale(this.$i18n.locale)
        const day = 1000 * 60 * 60 * 24
        const now = new Date()
        now.removeTime()
        const baseWeeks = 21
        if (this.range == "week") {
            this.dateRange.end = new Date(now.getTime() + day * (7 - now.getDay()))

            this.dateRange.start = new Date(this.dateRange.end.getTime() - day * 7 * baseWeeks)
        }

        window.addEventListener("resize", this.handleResize)
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize)
    },
    methods: {
        handleResize: throttle(function() {
            this.updateHeight()
        }, 300),
        updateHeight(trapsStats) {
            let height = (this.trapsStats || trapsStats).length * 25
            if (window.innerHeight <= height + 1024) {
                if (window.innerWidth >= 700) {
                    this.chartHeight = `${height / 2 + 300}`
                } else {
                    this.chartHeight = `${height + 300}`
                }
            } else {
                this.chartHeight = "600"
            }
        },
        generateColors(traps) {
            for (const trap of traps) {
                if (!(trap.trap._id in this.colors)) {
                    this.colors[trap.trap._id] = randomColor({ luminosity: "dark", hue: "random" })
                }
            }
        },

        async exportStats() {
            this.exportLoading = true
            try {
                const {
                    data: { exportTrapsStats },
                } = await this.$apollo.query({
                    fetchPolicy: "no-cache",
                    query: require("@/graphql/statistics/exportTrapsStats.gql"),
                    variables: {
                        categories: this.categoryFilter,
                        plan: this.planFilter,
                        endDate: this.dateRange.end.toISOString(),
                        startDate: this.dateRange.start.toISOString(),
                        range: this.range,
                        locale: navigator.language,
                    },
                })
                download(exportTrapsStats, `${this.$i18n.t("label.average")}.csv`)
            } catch (e) {}
            this.exportLoading = false
        },
    },
    computed: {
        ...mapGetters({ fullLocale: "user/fullLocale" }),
        stats() {
            if (!this.trapsStats) {
                return null
            }

            return {
                datasets: this.trapsStats.map(t => {
                    const data = []

                    for (const key of this.labels) {
                        data.push({
                            x: Number(key),
                            y: t.catches[t.labels.indexOf(key)] || 0,
                        })
                    }
                    return {
                        label: t.trap.name,
                        stack: "asd",
                        backgroundColor: this.colors[t.trap._id],
                        borderColor: this.colors[t.trap._id],
                        data,
                    }
                }),
            }
        },
        labels() {
            const labels = []

            for (const trap of this.trapsStats) {
                for (const label of trap.labels) {
                    if (labels.indexOf(label) === -1) {
                        labels.push(label)
                    }
                }
            }
            labels.sort()
            return labels
        },
        statsBattery() {
            if (!this.trapsStats) {
                return null
            }

            return {
                datasets: this.trapsStats.map(t => {
                    const data = []
                    for (const key in t.labels) {
                        data.push({
                            x: Number(t.labels[key]),
                            y: Math.round(t.batteries[key]),
                        })
                    }
                    return {
                        trap: t.trap,
                        label: t.trap.name,
                        backgroundColor: "transparent",
                        borderColor: this.colors[t.trap._id],
                        pointBackgroundColor: this.colors[t.trap._id],
                        data,
                    }
                }),
            }
        },
        statsSignal() {
            if (!this.trapsStats) {
                return null
            }

            return {
                datasets: this.trapsStats.map(t => {
                    const data = []
                    for (const key in t.labels) {
                        data.push({
                            x: Number(t.labels[key]),
                            y: Math.round(t.signals[key]),
                        })
                    }
                    return {
                        trap: t.trap,
                        label: t.trap.name,
                        backgroundColor: "transparent",
                        borderColor: this.colors[t.trap._id],
                        pointBackgroundColor: this.colors[t.trap._id],
                        data,
                    }
                }),
            }
        },
        globalSignalStats() {
            if (!this.trapsStats) {
                return null
            }
            const data = []
            for (const key of this.labels) {
                let counter = 0
                let average = 0
                for (const trap of this.trapsStats) {
                    const index = trap.labels.indexOf(key)
                    if (index !== -1) {
                        counter += 1
                        average += trap.signals[index]
                    }
                }
                data.push({
                    x: Number(key),
                    y: Math.round(average / counter),
                })
            }
            return {
                datasets: [{ backgroundColor: "transparent", data }],
            }
        },
        globalBatteryStats() {
            if (!this.trapsStats) {
                return null
            }
            const data = []
            for (const key of this.labels) {
                let counter = 0
                let average = 0
                for (const trap of this.trapsStats) {
                    const index = trap.labels.indexOf(key)
                    if (index !== -1) {
                        counter += 1
                        average += trap.batteries[index]
                    }
                }
                data.push({
                    x: Number(key),
                    y: Math.round(average / counter),
                })
            }
            return {
                datasets: [{ backgroundColor: "transparent", data }],
            }
        },
        globalTemperatureStats() {
            if (!this.trapsStats) {
                return null
            }
            const data = []
            for (const key of this.labels) {
                let counter = 0
                let average = 0
                for (const trap of this.trapsStats) {
                    const index = trap.labels.indexOf(key)
                    if (index !== -1) {
                        counter += 1
                        average += trap.temperatures[index]
                    }
                }
                data.push({
                    x: Number(key),
                    y: Math.round((average / counter) * 100) / 100,
                })
            }
            return {
                datasets: [{ backgroundColor: "transparent", data }],
            }
        },
        statsTemperatures() {
            if (!this.trapsStats) {
                return null
            }

            return {
                datasets: this.trapsStats.map(t => {
                    const data = []
                    for (const key in t.labels) {
                        data.push({
                            x: Number(t.labels[key]),
                            y: Math.round(t.temperatures[key] * 100) / 100,
                        })
                    }
                    return {
                        trap: t.trap,
                        label: t.trap.name,
                        backgroundColor: "transparent",
                        borderColor: this.colors[t.trap._id],
                        pointBackgroundColor: this.colors[t.trap._id],
                        data,
                    }
                }),
            }
        },
        statsTotalCatches() {
            if (!this.trapsStats) {
                return null
            }

            return {
                labels: this.trapsStats.map(t => t.trap.name),
                datasets: [
                    {
                        backgroundColor: this.trapsStats.map(t => this.colors[t.trap._id]),
                        data: this.trapsStats.map(t => t.totalCatches),
                    },
                ],
            }
        },

        totalCatched() {
            return this.trapsStats.reduce((a, t) => (a += t.totalCatches), 0)
        },
    },

    apollo: {
        trapsStats: {
            query: require("@/graphql/statistics/trapsStats.gql"),
            variables() {
                return {
                    categories: this.categoryFilter,
                    plan: this.planFilter,
                    endDate: this.dateRange.end.toISOString(),
                    startDate: this.dateRange.start.toISOString(),
                    range: this.range,
                }
            },
            update({ trapsStats }) {
                this.unit = this.calculateUnit()
                this.updateHeight(trapsStats)
                this.generateColors(trapsStats)
                return trapsStats
            },
            skip() {
                return !this.dateRange.end || !this.dateRange.start
            },
        },
    },
}
</script>

<style>
.Chart h2 {
    margin-top: 20px;
    margin-bottom: 10px;
    text-align: center;
}
</style>
